<template>
  <div v-show="show" class="member-user">
    <div class="tool-bar">
      <member-user-filter v-bind:condition="condition" v-on="$listeners" />
    </div>
    <div class="title-bar">
      <h1>{{ usersCount }}</h1>
      <div class="selector">
        <member-user-sort-rule-selector
          v-bind:sort-rule="sortRule"
          v-bind:condition="condition"
          v-on="$listeners"
        />
        <member-user-option-menu
          v-bind:users="users"
          v-bind:additionalItems="additionalItems"
          v-bind:is-simple="isCardSimple"
          v-bind:base-date="baseDate"
          v-on:items-changed="$_onItemsChanged"
          v-on:is-simple-changed="$_onIsSimpleChanged"
        />
      </div>
    </div>
    <div class="users-list-container">
      <loading-mask v-bind:isLoading="isLoading">
        <recycle-scroller
          ref="scroller"
          class="users-list"
          v-bind:items="usersForCard"
          v-bind:item-size="cardHeight"
          v-slot="{ item }"
        >
          <card-member
            class="user-item"
            v-bind="item"
            v-on:click="$_onCardClick(item.id)"
            v-on:usercard-history-click="$_onHistoryClick"
            v-on:usercard-username-click="$_onUserNameClick"
          />
        </recycle-scroller>
      </loading-mask>
    </div>
    <!-- カード高さ計測用隠し要素 -->
    <div class="hidden">
      <card-member
        v-if="usersForCard.length"
        ref="card"
        class="user-item"
        v-bind="usersForCard[0]"
      />
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'
import MemberUserFilter from '@/components/member/AppMemberUserFilter.vue'
import MemberUserSortRuleSelector from '@/components/member/AppMemberUserSortRuleSelector.vue'
import MemberUserOptionMenu from '@/components/member/AppMemberUserOptionMenu.vue'
import { RecycleScroller } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import CardMember from '@/components/member/AppCardMember.vue'
import { FUNCTION_TYPE } from '@/constants.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// 初期カード高さ(px)
const INITIAL_CARD_HEIGHT = 59

/**
 * 地図画面 - ユーザータブ
 */
export default {
  name: 'AppMemberUser',
  components: {
    MemberUserFilter,
    MemberUserSortRuleSelector,
    MemberUserOptionMenu,
    RecycleScroller,
    CardMember,
  },
  inheritAttrs: false,
  props: {
    /**
     * 表示フラグ
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー情報
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
    /**
     * ソートルール
     */
    sortRule: {
      type: Object,
      default: () => {},
    },
    /**
     * 追加表示項目
     */
    additionalItems: {
      type: Array,
      default: () => [],
    },
    /**
     * カード簡易表示フラグ
     */
    isCardSimple: {
      type: Boolean,
      default: false,
    },
    /**
     * 測位中フラグ
     */
    isPositioning: {
      type: Boolean,
      default: null,
    },
    /**
     * ロード中フラグ
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const baseDate = inject('baseDate')
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError, baseDate }
  },
  data: function () {
    return {
      // カード高さ(px)
      cardHeight: INITIAL_CARD_HEIGHT,
    }
  },
  computed: {
    /**
     * 分析が利用可能か？
     * @type {Boolean}
     */
    isAnalyzeAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.ANALYZE)
    },
    /**
     * メンバーの件数
     * @type {String}
     */
    usersCount: function () {
      const count = (this.users && this.users.length) || 0
      return this.$tc('appMemberUser.users', count)
    },
    /**
     * カード用ユーザーデータ
     * @type {Array.<Object>}
     */
    usersForCard: function () {
      return this.users.map((user) => ({
        id: user.id,
        userId: user.id,
        roleId: user.role_id,
        userName: user.user_name,
        groups: user.groups,
        profileImageAttachFileType: user.profile_image
          ? user.profile_image.attach_file_type
          : null,
        items: user.additional_items,
        // 以下ユーザーデータとは関係無いが RecycleScroller を正常にレンダリングするために
        // カードの表示に必要な情報はすべて当 Object に含める必要があるため追加する
        baseDate: this.baseDate,
        simple: this.isCardSimple,
        history: this.isAnalyzeAvailable,
        // 各カードが RecycleScroller 内で使い回されないように設定
        type: `card_${user.id}`,
      }))
    },
  },
  watch: {
    // カードの高さが変わり得るデータに変化があったら仮想スクロール用にカード高さを再取得
    usersForCard: {
      immediate: true,
      handler: function (value) {
        if (this.show && value.length) {
          this.$nextTick().then(() => {
            this.$_getCardHeight()
          })
        }
      },
    },
    // 表示フラグがtrueになった場合も仮想スクロール用にカード高さを再取得
    show: function (value) {
      if (value && this.usersForCard.length) {
        this.$nextTick().then(() => {
          this.$_getCardHeight()
        })
      }
    },
  },
  methods: {
    /**
     * カードの高さを取得する
     */
    $_getCardHeight: function () {
      const rect = this.$refs['card']?.$el.getBoundingClientRect()
      if (rect) {
        this.cardHeight = rect.height
        this.$log.debug('[DEBUG] height of user card:', this.cardHeight)
      }
    },

    /**
     * 表示アイテム選択変更時処理
     * @param {Array.<Number>} items 選択表示アイテム
     */
    $_onItemsChanged: function (items) {
      // イベント名を変換する
      /**
       * 表示アイテム選択変更イベント
       * @param {Array.<Number>} items 選択表示アイテム
       */
      this.$emit('additional-items-changed', items)
    },

    /**
     * 簡易表示フラグ変更時処理
     * @param {Boolean} isSimple 簡易表示フラグ
     */
    $_onIsSimpleChanged: function (isSimple) {
      // イベント名を変換する
      /**
       * 簡易表示フラグ変更イベント
       * @param {Boolean} isSimple 簡易表示フラグ
       */
      this.$emit('is-card-simple-changed', isSimple)
    },

    /**
     * メンバー一覧のカードをクリックした時の処理
     * @param {Number} id ユーザID
     */
    $_onCardClick: function (id) {
      const user = this.users.find((user) => user.id === id)
      if (user) {
        /**
         * メンバーリストのカードクリックイベント
         * @param {Object} user ユーザデータ
         */
        this.$emit('user-card-click', user)
      }
    },

    /**
     * メンバー一覧のユーザー名をクリックした時の処理
     * @param {Number} id ユーザID
     */
    $_onUserNameClick: function (id) {
      // ユーザー詳細を開くためIDをstoreにcommit
      this.$store.dispatch('updateProfileUserId', id)
    },

    /**
     * メンバー一覧の履歴ボタンをクリックした時の処理
     * @param {Number} id ユーザID
     * @param {Event} event Event
     */
    $_onHistoryClick: function (id, event) {
      const user = this.users.find((m) => m.id === id)
      this.$router.push({
        name: 'analyze',
        params: {
          initialType: 'track',
          initialUsers: [{ id, name: user.user_name }],
        },
      })
      event.stopPropagation()
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/tool-bar';
@use '../../assets/scss/parts/title-bar';

.member-user {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto auto 1fr;
}

.tool-bar {
  height: auto;
  border-top-right-radius: 8px;
  background-color: #ffffff;
  overflow-y: hidden;
}

.title-bar {
  padding: 0 10px 0 16px;
  display: flex;
  justify-content: space-between;
  column-gap: 20px;

  .selector {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 8px;

    ::v-deep .member-user-sort-rule-selector {
      .field-selector {
        max-width: 11.5rem;
      }
      .order-selector {
        min-width: 6rem;
      }
    }
  }
}

.users-list-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.users-list {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  @include mixin-scrollbar;
  &::-webkit-scrollbar {
    height: 0;
  }
}

.user-item {
  cursor: pointer;
  padding: 10px 10px 8px 10px;
  border-bottom: 1px solid #eaecef;
  border-right: 1px solid #eaecef;

  &:hover {
    background-color: #f9fafc;
  }
}

.hidden {
  z-index: -1;
  opacity: 0;
  visibility: hidden;
  position: fixed;
  top: -999px;
  left: -999px;
  pointer-events: none;
}
</style>
